import { useCallback, useRef, useState } from 'react'

export interface Turn {
  role: 'user' | 'assistant'
  content: string
}

export interface Episode {
  id: string
  title: string
  synopsis: string
}

/**
 * Client for obsidia.work's chat endpoint.
 *
 * `fetch` + a stream reader rather than EventSource, because the request POSTs
 * conversation history and EventSource is GET-only.
 *
 * `turnsUsed` is echoed back by the server and mirrored here so the UI can show
 * how many free turns remain. It is display state, not the paywall — the server
 * decides, and the real ceiling is Atlas's monthly quota on the teaser pool.
 */
export function useObsidia() {
  const [turns, setTurns] = useState<Turn[]>([])
  const [streaming, setStreaming] = useState(false)
  const [speaking, setSpeaking] = useState(false)
  // Muting is client-side only and also skips the request, so a muted visitor
  // never spends the account's ElevenLabs characters.
  const [muted, setMuted] = useState(false)
  // Autoplay is only reliable on an element the user has already started once,
  // so we keep ONE element for the session and prime it inside the send gesture
  // rather than constructing a new Audio() two awaits later.
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const blobRef = useRef<string | null>(null)
  // Set when the browser refuses playback anyway, so the UI can offer a manual
  // control instead of just being silent.
  const [blocked, setBlocked] = useState(false)
  // Why she is silent, when it isn't autoplay. `if (!res.ok) return` used to
  // discard this, which made a spent voice budget look identical to success.
  const [voiceError, setVoiceError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [paywalled, setPaywalled] = useState(false)
  const [turnsUsed, setTurnsUsed] = useState(0)
  const [teaserTurns, setTeaserTurns] = useState(3)
  const abortRef = useRef<AbortController | null>(null)

  const releaseBlob = () => {
    if (blobRef.current) {
      URL.revokeObjectURL(blobRef.current)
      blobRef.current = null
    }
  }

  const stopAudio = useCallback(() => {
    audioRef.current?.pause()
    releaseBlob()
    setSpeaking(false)
  }, [])

  /** Called from the submit handler — a real user gesture — to unlock playback. */
  const primeAudio = useCallback(() => {
    if (!audioRef.current) {
      const el = new Audio()
      el.preload = 'auto'
      audioRef.current = el
    }
    const el = audioRef.current
    if (el.dataset.primed === '1') return
    // A muted no-op play marks the element as user-started. Guarded by a flag
    // because this promise can resolve AFTER real playback has begun — an
    // unguarded `el.pause()` in here would kill the reply it was meant to enable.
    el.muted = true
    void el
      .play()
      .then(() => {
        if (el.dataset.primed !== 'playing') el.pause()
        el.muted = false
        el.dataset.primed = '1'
      })
      .catch(() => {
        el.muted = false
        el.dataset.primed = '1'
      })
  }, [])

  const stop = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    setStreaming(false)
    stopAudio()
  }, [stopAudio])

  /**
   * Speak a finished reply. Best-effort by design: if the character budget is
   * gone or synthesis fails, the transcript is already on screen, so the page
   * degrades to text rather than surfacing an error.
   */
  const speak = useCallback(
    async (text: string) => {
      if (!text.trim()) return
      setVoiceError(null)
      try {
        const res = await fetch('/api/speak', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ text }),
        })
        if (!res.ok) {
          setVoiceError(
            res.status === 429
              ? 'Her voice budget is spent for this month.'
              : res.status === 403
                ? 'Her voice needs a subscription.'
                : 'Her voice is unavailable right now.',
          )
          setSpeaking(false)
          return
        }
        releaseBlob()
        const url = URL.createObjectURL(await res.blob())
        blobRef.current = url
        const el = audioRef.current ?? new Audio()
        audioRef.current = el
        // Tells a late-resolving prime() not to pause us.
        el.dataset.primed = 'playing'
        el.src = url
        el.onended = () => setSpeaking(false)
        // play() resolving only means playback STARTED. If the media fails to
        // load or decode, the error arrives here and nowhere else.
        el.onerror = () => {
          const code = el.error?.code
          setVoiceError(`Audio failed to load (media error ${code ?? '?'}).`)
          setSpeaking(false)
        }
        setSpeaking(true)
        try {
          await el.play()
          setBlocked(false)
        } catch {
          // Do NOT swallow this. A blocked autoplay is indistinguishable from
          // "TTS is broken" if it fails silently — which is exactly how this
          // shipped the first time.
          setSpeaking(false)
          setBlocked(true)
        }
      } catch {
        setVoiceError('Her voice is unavailable right now.')
        setSpeaking(false)
      }
    },
    [],
  )

  const send = useCallback(
    async (text: string, episode?: Episode) => {
      const message = text.trim()
      if (!message || streaming || paywalled) return

      setError(null)
      setStreaming(true)

      const history = [...turns, { role: 'user' as const, content: message }]
      setTurns([...history, { role: 'assistant', content: '' }])

      const controller = new AbortController()
      abortRef.current = controller

      const append = (chunk: string) =>
        setTurns((prev) => {
          const next = [...prev]
          const last = next[next.length - 1]
          if (last?.role === 'assistant') next[next.length - 1] = { ...last, content: last.content + chunk }
          return next
        })

      /** Drop the empty assistant turn so the transcript isn't left with a gap. */
      const dropPending = () =>
        setTurns((prev) => (prev[prev.length - 1]?.content === '' ? prev.slice(0, -1) : prev))

      try {
        const res = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ messages: history, episode, turnsUsed }),
          signal: controller.signal,
        })

        if (res.status === 402) {
          const detail = await res.json().catch(() => null)
          setPaywalled(true)
          if (typeof detail?.teaserTurns === 'number') setTeaserTurns(detail.teaserTurns)
          dropPending()
          return
        }

        if (!res.ok || !res.body) {
          const detail = await res.json().catch(() => null)
          throw new Error(detail?.message ?? 'She dropped off the wire.')
        }

        const reader = res.body.pipeThrough(new TextDecoderStream()).getReader()
        let buf = ''
        let full = ''
        for (;;) {
          const { done, value } = await reader.read()
          if (done) break
          buf += value
          let i: number
          // SSE frames end in a blank line and can split across chunks.
          while ((i = buf.indexOf('\n\n')) !== -1) {
            const frame = buf.slice(0, i)
            buf = buf.slice(i + 2)
            const event = frame.match(/^event: (.*)$/m)?.[1]
            const raw = frame.match(/^data: (.*)$/m)?.[1]
            if (!event || !raw) continue
            const data = JSON.parse(raw)
            if (event === 'delta') {
              full += data.text
              append(data.text)
            }
            else if (event === 'refusal' || event === 'error') setError(data.message)
            else if (event === 'done') {
              if (typeof data.turnsUsed === 'number') setTurnsUsed(data.turnsUsed)
              if (typeof data.teaserTurns === 'number') setTeaserTurns(data.teaserTurns)
            }
          }
        }
        if (!muted && full.trim()) void speak(full)
      } catch (err) {
        if ((err as Error).name === 'AbortError') return
        setError((err as Error).message)
        dropPending()
      } finally {
        setStreaming(false)
        abortRef.current = null
      }
    },
    [turns, streaming, paywalled, turnsUsed, muted, speak],
  )

  return {
    turns,
    streaming,
    error,
    paywalled,
    turnsUsed,
    teaserTurns,
    remaining: Math.max(0, teaserTurns - turnsUsed),
    send,
    stop,
    primeAudio,
    speaking,
    muted,
    blocked,
    voiceError,
    replay: () => {
      const el = audioRef.current
      if (el && blobRef.current) {
        setSpeaking(true)
        void el.play().then(() => setBlocked(false)).catch(() => setSpeaking(false))
      }
    },
    toggleMute: () => {
      setMuted((m) => {
        if (!m) stopAudio()
        return !m
      })
    },
  }
}
